import React, { useContext, useMemo } from 'react';
import { emptyArray } from 'ui-components/default-props/empty';

import { ChildrenPropType } from '../../prop-types/children';
import { useWalletBalancesMap } from '../hooks/use-wallet-balances-map';
import { useWalletSettingsMap } from '../hooks/use-wallet-settings-map';
import { Providers } from '../../enums/providers';
import { walletSettingsEnum } from '../../enums/wallet-settings';

const WalletDataContext = React.createContext();
export const useWalletDataContext = () => useContext(WalletDataContext);

export const WalletDataContextProvider = ({ children }) => {
  const { loading: loadingSettingsMap, settingsMap } = useWalletSettingsMap();

  const { loading: loadingBalancesMap, balancesMap } = useWalletBalancesMap();

  const value = useMemo(
    () => ({
      loadingSettingsMap,
      settingsMap,
      loadingBalancesMap,
      balancesMap,
    }),
    [loadingSettingsMap, settingsMap, loadingBalancesMap, balancesMap],
  );

  return <WalletDataContext.Provider value={value}>{children}</WalletDataContext.Provider>;
};

WalletDataContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
};

// NOTE: the new carriers page UI does not render wallet level settings
export const useWalletSetting = (walletId, walletSettingId, enabledByFeatureFlag = true) => {
  const { settingsMap } = useWalletDataContext();
  const walletSettings = settingsMap?.[walletId]?.settingTypes || emptyArray;

  const canUseSetting = enabledByFeatureFlag && walletSettings.includes(walletSettingId);
  return { canUseSetting };
};

export const useCanProviderViewStampsReports = (walletId, providerId) => {
  return useWalletSetting(walletId, walletSettingsEnum.reportsLink, true) && providerId === Providers.Stamps.labelProviderId;
};

export const useWalletSellerLabelProviderSetting = (walletId, sellerLabelProviderId, sellerLabelProviderSettingId) => {
  const { settingsMap } = useWalletDataContext();
  const walletSellerProviderSettings = settingsMap?.[walletId]?.sellerLabelProviderSettings || emptyArray;
  const sellerProviderSettingsMap = walletSellerProviderSettings.reduce(
    (acc, setting) => ({
      ...acc,
      [setting.sellerLabelProviderId]: setting.settingTypes || [],
    }),
    {},
  );
  const sellerLabelProviderSettings = sellerProviderSettingsMap[sellerLabelProviderId] || emptyArray;

  const canUseSetting = sellerLabelProviderSettings.includes(sellerLabelProviderSettingId);
  return { canUseSetting };
};

export const useWalletBalance = (walletId) => {
  const { balancesMap, loadingBalancesMap } = useWalletDataContext();
  return {
    balance: balancesMap?.[walletId]?.balance,
    loading: loadingBalancesMap,
  };
};
